import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { SubscriptionType } from '../types/subscription';
import {
  getPermissionBySubscriptionType,
  getUserSubscriptionPermissions,
  hasPermissionForSubscription,
  PermissionLevel,
} from '../utilities/permissions';
import { useAuth } from './AuthContext';
import { captureHandledError } from '../utilities/datadog';

type PermissionsContextType = {
  permissions: SubscriptionType[];
  setPermissions: React.Dispatch<React.SetStateAction<SubscriptionType[]>>;
  isLoading: boolean;
  refreshPermissions: () => Promise<SubscriptionType[]>;
  getPermission: (subscriptionType: string) => SubscriptionType | null;
  hasPermission: (
    subscriptionType: string,
    permission: PermissionLevel
  ) => boolean;
};

const PermissionsContext = createContext<PermissionsContextType | undefined>(
  undefined
);

export const PermissionsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { organization, user } = useAuth();
  const [permissions, setPermissions] = useState<SubscriptionType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const orgId = organization?.id ?? user?.org_id;
  const userId = user?.id;

  const loadPermissions = useCallback(async (): Promise<SubscriptionType[]> => {
    if (!orgId || !userId) {
      setPermissions([]);
      setIsLoading(false);
      return [];
    }

    setIsLoading(true);

    try {
      const userPermissions = await getUserSubscriptionPermissions(
        orgId,
        userId
      );
      setPermissions(userPermissions);
      return userPermissions;
    } catch (error) {
      captureHandledError('Error fetching user permissions', error, {
        org_id: orgId,
        user_id: userId,
      });
      setPermissions([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [orgId, userId]);

  useEffect(() => {
    let cancelled = false;

    if (!orgId || !userId) {
      setPermissions([]);
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);

    (async () => {
      try {
        const userPermissions = await getUserSubscriptionPermissions(
          orgId,
          userId
        );
        if (!cancelled) setPermissions(userPermissions);
      } catch (error) {
        captureHandledError('Error fetching user permissions', error, {
          org_id: orgId,
          user_id: userId,
        });
        if (!cancelled) setPermissions([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId, userId]);

  const getPermission = useCallback(
    (subscriptionType: string) =>
      getPermissionBySubscriptionType(permissions, subscriptionType),
    [permissions]
  );

  const hasPermission = useCallback(
    (subscriptionType: string, permission: PermissionLevel) =>
      hasPermissionForSubscription(permissions, subscriptionType, permission),
    [permissions]
  );

  const value = useMemo(
    () => ({
      permissions,
      setPermissions,
      isLoading,
      refreshPermissions: loadPermissions,
      getPermission,
      hasPermission,
    }),
    [permissions, isLoading, loadPermissions, getPermission, hasPermission]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = (): PermissionsContextType => {
  const context = useContext(PermissionsContext);

  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }

  return context;
};
